import React, { useState, useEffect, useCallback } from 'react';
import { claimsApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Pagination } from '../../components/Pagination';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { AlertTriangle, Loader2, Eye, Smartphone, Plus } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  SUBMITTED: 'bg-blue-50 text-blue-700 border-blue-200',
  UNDER_REVIEW: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  REPAIR_IN_PROGRESS: 'bg-purple-50 text-purple-700 border-purple-200',
  RESOLVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
};

const ISSUE_TYPES = {
  FUNCTIONAL_DEFECT: 'Functional Defect',
  SCREEN_DAMAGE: 'Screen Damage',
  LIQUID_DAMAGE: 'Liquid Damage',
  OTHER: 'Other',
};

export default function ClaimsPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateForm, setUpdateForm] = useState({ status: '', admin_notes: '', resolution_notes: '' });
  const [updating, setUpdating] = useState(false);
  const [showFileClaimDialog, setShowFileClaimDialog] = useState(false);
  const [claimForm, setClaimForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    device_imei: '', warranty_certificate: '', issue_description: '', issue_type: 'FUNCTIONAL_DEFECT',
  });
  const [filing, setFiling] = useState(false);
  const isAdmin = user?.role === 'SUPER_ADMIN';
  const canUpdate = isAdmin;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data: res } = await claimsApi.list(params);
      setData(res);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load claims');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openUpdate = (claim) => {
    setSelectedClaim(claim);
    setUpdateForm({
      status: claim.status,
      admin_notes: claim.admin_notes || '',
      resolution_notes: claim.resolution_notes || '',
    });
    setShowUpdateDialog(true);
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await claimsApi.update(selectedClaim.id, updateForm);
      toast.success('Claim updated');
      setShowUpdateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update claim');
    } finally {
      setUpdating(false);
    }
  };

  const handleFileClaim = async () => {
    if (!claimForm.customer_name || !claimForm.customer_phone || !claimForm.device_imei || !claimForm.issue_description) {
      toast.error('Please fill all required fields');
      return;
    }
    setFiling(true);
    try {
      await claimsApi.create(claimForm);
      toast.success('Claim filed successfully');
      setShowFileClaimDialog(false);
      setClaimForm({ customer_name: '', customer_phone: '', customer_email: '', device_imei: '', warranty_certificate: '', issue_description: '', issue_type: 'FUNCTIONAL_DEFECT' });
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to file claim');
    } finally {
      setFiling(false);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }} data-testid="claims-heading">Claims</h1>
            <p className="text-slate-600 mt-1">Manage warranty claims from customers</p>
          </div>
          <div className="flex items-center gap-3">
            {!isAdmin && (
              <Button onClick={() => setShowFileClaimDialog(true)} className="bg-[#005DE8] hover:bg-[#0047B3]" data-testid="file-claim-btn">
                <Plus className="h-4 w-4 mr-2" />File Claim
              </Button>
            )}
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-44" data-testid="claims-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="SUBMITTED">Submitted</SelectItem>
              <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="REPAIR_IN_PROGRESS">In Repair</SelectItem>
              <SelectItem value="RESOLVED">Resolved</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
            </SelectContent>
          </Select>
          </div>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" /></div>
            ) : data.items.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100">
                      <TableHead>Customer</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Issue</TableHead>
                      <TableHead>Warranty</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map(claim => (
                      <TableRow key={claim.id} className="table-row-hover">
                        <TableCell>
                          <p className="font-medium text-slate-900">{claim.customer_name}</p>
                          <p className="text-sm text-slate-500">{claim.customer_phone}</p>
                        </TableCell>
                        <TableCell>
                          {claim.device_brand ? (
                            <div className="flex items-center gap-2">
                              <Smartphone className="h-4 w-4 text-slate-400" />
                              <span>{claim.device_brand} {claim.device_model}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400">IMEI: {claim.device_imei}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{ISSUE_TYPES[claim.issue_type] || claim.issue_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={claim.warranty_valid
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 border'
                            : 'bg-red-50 text-red-700 border-red-200 border'
                          }>
                            {claim.warranty_valid ? 'Valid' : 'Invalid/None'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_COLORS[claim.status] || ''} border`}>{claim.status?.replace(/_/g, ' ')}</Badge>
                        </TableCell>
                        <TableCell className="text-slate-600 text-sm">{formatDateTime(claim.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => openUpdate(claim)} className="h-8" data-testid={`claim-update-${claim.id}`}>
                            <Eye className="h-4 w-4 mr-1" /> {canUpdate ? 'Review' : 'View'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination page={data.page} pages={data.pages} total={data.total} onPageChange={setPage} />
              </>
            ) : (
              <div className="text-center py-16">
                <AlertTriangle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No claims found</h3>
                <p className="text-slate-500 mt-1">Customer claims will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Update Claim Dialog */}
        <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Review Claim</DialogTitle>
            </DialogHeader>
            {selectedClaim && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 rounded-lg p-4">
                  <div><span className="text-slate-500">Customer:</span> <span className="font-medium">{selectedClaim.customer_name}</span></div>
                  <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{selectedClaim.customer_phone}</span></div>
                  <div><span className="text-slate-500">IMEI:</span> <span className="font-mono text-xs">{selectedClaim.device_imei}</span></div>
                  <div><span className="text-slate-500">Certificate:</span> <span className="font-mono text-xs">{selectedClaim.warranty_certificate || '-'}</span></div>
                  <div><span className="text-slate-500">Issue:</span> <span>{ISSUE_TYPES[selectedClaim.issue_type] || selectedClaim.issue_type}</span></div>
                  <div><span className="text-slate-500">Warranty:</span> <Badge className={selectedClaim.warranty_valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>{selectedClaim.warranty_valid ? 'Valid' : 'Invalid'}</Badge></div>
                  <div className="col-span-2"><span className="text-slate-500">Description:</span> <p className="mt-1">{selectedClaim.issue_description}</p></div>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={updateForm.status} onValueChange={(v) => setUpdateForm({ ...updateForm, status: v })} disabled={!canUpdate}>
                    <SelectTrigger data-testid="claim-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SUBMITTED">Submitted</SelectItem>
                      <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
                      <SelectItem value="APPROVED">Approved</SelectItem>
                      <SelectItem value="REPAIR_IN_PROGRESS">Repair In Progress</SelectItem>
                      <SelectItem value="RESOLVED">Resolved</SelectItem>
                      <SelectItem value="REJECTED">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Admin Notes</Label>
                  <Textarea value={updateForm.admin_notes} onChange={(e) => setUpdateForm({ ...updateForm, admin_notes: e.target.value })} rows={2} data-testid="claim-admin-notes" disabled={!canUpdate} />
                </div>
                <div className="space-y-2">
                  <Label>Resolution Notes</Label>
                  <Textarea value={updateForm.resolution_notes} onChange={(e) => setUpdateForm({ ...updateForm, resolution_notes: e.target.value })} rows={2} data-testid="claim-resolution-notes" disabled={!canUpdate} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>Close</Button>
              {canUpdate && (
                <Button onClick={handleUpdate} disabled={updating} className="bg-[#005DE8] hover:bg-[#0047B3]" data-testid="claim-update-submit">
                  {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Update Claim
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* File Claim Dialog */}
        <Dialog open={showFileClaimDialog} onOpenChange={setShowFileClaimDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>File Claim on Behalf of Customer</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Customer Name *</Label><Input value={claimForm.customer_name} onChange={(e) => setClaimForm({ ...claimForm, customer_name: e.target.value })} data-testid="fc-name" /></div>
                <div><Label className="text-xs">Phone *</Label><Input value={claimForm.customer_phone} onChange={(e) => setClaimForm({ ...claimForm, customer_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} data-testid="fc-phone" /></div>
              </div>
              <div><Label className="text-xs">Email (optional)</Label><Input type="email" value={claimForm.customer_email} onChange={(e) => setClaimForm({ ...claimForm, customer_email: e.target.value })} data-testid="fc-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Device IMEI *</Label><Input value={claimForm.device_imei} onChange={(e) => setClaimForm({ ...claimForm, device_imei: e.target.value.replace(/\D/g, '').slice(0, 15) })} data-testid="fc-imei" /></div>
                <div><Label className="text-xs">Certificate #</Label><Input value={claimForm.warranty_certificate} onChange={(e) => setClaimForm({ ...claimForm, warranty_certificate: e.target.value })} data-testid="fc-cert" /></div>
              </div>
              <div>
                <Label className="text-xs">Issue Type *</Label>
                <Select value={claimForm.issue_type} onValueChange={(v) => setClaimForm({ ...claimForm, issue_type: v })}>
                  <SelectTrigger data-testid="fc-issue-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FUNCTIONAL_DEFECT">Functional Defect</SelectItem>
                    <SelectItem value="SCREEN_DAMAGE">Screen Damage</SelectItem>
                    <SelectItem value="LIQUID_DAMAGE">Liquid Damage</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Issue Description *</Label><Textarea value={claimForm.issue_description} onChange={(e) => setClaimForm({ ...claimForm, issue_description: e.target.value })} rows={3} data-testid="fc-desc" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFileClaimDialog(false)}>Cancel</Button>
              <Button onClick={handleFileClaim} disabled={filing} className="bg-[#005DE8]" data-testid="fc-submit">
                {filing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Submit Claim
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
